/**
 * Assistant de questions.
 *
 * La cliente tape sa question librement ; le script la compare aux questions
 * saisies dans l'editeur de theme et renvoie la reponse correspondante.
 *
 * Aucune requete reseau, aucune donnee transmise, aucun texte genere :
 * l'assistant ne peut afficher que des reponses ecrites par la boutique.
 * Quand il ne trouve pas, il le dit et propose ses questions.
 */

// Mots trop courants pour porter du sens, en anglais et en francais.
const STOP_WORDS = new Set([
  'the', 'a', 'an', 'and', 'or', 'of', 'to', 'in', 'on', 'for', 'is', 'are',
  'do', 'does', 'did', 'can', 'could', 'will', 'would', 'i', 'you', 'my',
  'your', 'it', 'this', 'that', 'with', 'have', 'has', 'how', 'what', 'when',
  'where', 'which', 'be', 'am', 'was', 'were', 'me', 'we', 'us', 'not', 'any',
  'le', 'la', 'les', 'un', 'une', 'des', 'du', 'de', 'et', 'ou', 'je', 'tu',
  'il', 'elle', 'vous', 'nous', 'mon', 'ma', 'mes', 'est', 'sont', 'que',
  'qui', 'quoi', 'comment', 'quand', 'pour', 'avec', 'dans', 'sur', 'pas',
  'ce', 'cette', 'ces', 'son', 'sa', 'ses', 'au', 'aux', 'en', 'par',
  'el', 'la', 'los', 'las', 'un', 'una', 'unos', 'unas', 'de', 'del', 'y',
  'o', 'que', 'como', 'cuando', 'donde', 'para', 'por', 'con', 'sin', 'mi',
  'mis', 'tu', 'tus', 'su', 'sus', 'es', 'son', 'esta', 'este', 'estos',
  'puedo', 'puede', 'tengo', 'hay', 'lo', 'se', 'me', 'te', 'al', 'no',
]);

function normalise(text) {
  return (text || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')   // retire les accents
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function tokenise(text) {
  return normalise(text)
    .split(' ')
    .filter((word) => word.length > 2 && !STOP_WORDS.has(word));
}

class ChatAssistant extends HTMLElement {
  connectedCallback() {
    this.log = this.querySelector('[data-chat-log]');
    this.launcher = this.querySelector('[data-chat-open]');
    this.closeButton = this.querySelector('[data-chat-close]');
    this.form = this.querySelector('[data-chat-form]');
    this.input = this.querySelector('[data-chat-input]');
    this.questionList = this.querySelector('[data-chat-questions]');

    this.entries = [...this.querySelectorAll('[data-chat-question]')].map((button) => ({
      button,
      answer: button.dataset.answer || '',
      questionTokens: new Set(tokenise(button.textContent)),
      keywordTokens: new Set(tokenise(button.dataset.keywords)),
      answerTokens: new Set(tokenise(button.dataset.answer)),
      questionText: normalise(button.textContent),
    }));

    this.launcher?.addEventListener('click', () => this.open());
    this.closeButton?.addEventListener('click', () => this.close());

    this.entries.forEach((entry) => {
      entry.button.addEventListener('click', () => {
        this.say('user', entry.button.textContent.trim());
        this.reply(entry);
      });
    });

    this.form?.addEventListener('submit', (event) => {
      event.preventDefault();
      this.ask(this.input.value);
    });

    this.onKeydown = (event) => {
      if (event.key === 'Escape' && this.dataset.open === 'true') this.close();
    };
    document.addEventListener('keydown', this.onKeydown);
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.onKeydown);
  }

  open() {
    this.dataset.open = 'true';
    this.input?.focus();
  }

  close() {
    this.dataset.open = 'false';
    this.launcher?.focus();
  }

  say(role, html) {
    const bubble = document.createElement('div');
    bubble.className = `chat-assistant__bubble chat-assistant__bubble--${role}`;
    bubble.innerHTML = html;
    this.log.appendChild(bubble);
    this.log.scrollTop = this.log.scrollHeight;
  }

  /** Note la proximite entre une question tapee et une entree connue. */
  score(entry, tokens, phrase) {
    let total = 0;

    tokens.forEach((token) => {
      if (entry.questionTokens.has(token)) total += 3;
      else if (entry.keywordTokens.has(token)) total += 3;
      else if (entry.answerTokens.has(token)) total += 1;
    });

    // La question posee reprend presque mot pour mot une question connue.
    if (phrase.length > 6 && entry.questionText.includes(phrase)) total += 4;

    return total;
  }

  ask(rawText) {
    const text = (rawText || '').trim();
    if (!text) return;

    this.say('user', text.replace(/</g, '&lt;'));
    this.input.value = '';

    const tokens = [...new Set(tokenise(text))];
    const phrase = normalise(text);

    const ranked = this.entries
      .filter((entry) => !entry.button.hidden || true)
      .map((entry) => ({ entry, value: this.score(entry, tokens, phrase) }))
      .sort((a, b) => b.value - a.value);

    const best = ranked[0];

    // En dessous du seuil, mieux vaut avouer que deviner.
    if (!best || best.value < 3) {
      this.fallback();
      return;
    }

    this.reply(best.entry);
  }

  reply(entry) {
    window.setTimeout(() => {
      this.say('bot', entry.answer);
      entry.button.hidden = true;
      this.refreshQuestions();
    }, 260);
  }

  fallback() {
    window.setTimeout(() => {
      this.say('bot', this.dataset.fallback || '');
      // On remet toutes les questions sous les yeux : c'est la meilleure aide.
      this.entries.forEach((entry) => {
        entry.button.hidden = false;
      });
      this.refreshQuestions();
    }, 260);
  }

  refreshQuestions() {
    if (!this.questionList) return;
    const remaining = this.querySelectorAll('[data-chat-question]:not([hidden])');
    this.questionList.hidden = remaining.length === 0;
  }
}

if (!customElements.get('chat-assistant')) {
  customElements.define('chat-assistant', ChatAssistant);
}
